import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublicArticles } from '../utils/api';
import SmartImage from '../components/SmartImage';
import { Card } from '../components/ui/card';
import { Calendar, User, Newspaper, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicArticles(50)
      .then(r => setArticles(r.data || []))
      .catch(() => toast.error('Error al cargar artículos'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando artículos...</div>;
  }

  const [feature, ...rest] = articles;

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="articles-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3">
            <Newspaper className="w-3.5 h-3.5" /> NOVEDADES
          </div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-3">Artículos & Noticias</h1>
          <p className="text-base text-muted-foreground">
            Lo último de IAASP Academy: novedades, anuncios y guías profesionales.
          </p>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            Aún no hay artículos publicados. ¡Vuelve pronto!
          </div>
        ) : (
          <>
            {feature && (
              <Link to={`/articulos/${feature.slug}`} data-testid="featured-article"
                className="group block mb-12 bg-white rounded-2xl border overflow-hidden hover:shadow-xl transition-all">
                <div className="grid md:grid-cols-2">
                  {feature.featured_image && (
                    <div className="aspect-video md:aspect-auto md:h-full overflow-hidden bg-gray-100">
                      <SmartImage
                        src={feature.featured_image}
                        alt={feature.title}
                        fallbackLabel={feature.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <span className="text-xs font-semibold text-accent mb-2">DESTACADO</span>
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-3 group-hover:text-accent transition-colors">
                      {feature.title}
                    </h2>
                    {feature.excerpt && <p className="text-muted-foreground mb-4 line-clamp-3">{feature.excerpt}</p>}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-6">
                      {feature.author_name && (
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{feature.author_name}</span>
                      )}
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(feature.created_at)}</span>
                    </div>
                    <span className="inline-flex items-center gap-2 text-accent font-semibold text-sm group-hover:gap-3 transition-all">
                      Leer artículo <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((a) => (
                <Link key={a.id} to={`/articulos/${a.slug}`} data-testid={`article-card-${a.id}`}
                  className="group bg-white rounded-2xl border overflow-hidden hover:shadow-lg hover:border-accent/40 transition-all">
                  {a.featured_image ? (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <SmartImage
                        src={a.featured_image}
                        alt={a.title}
                        fallbackLabel={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                      <Newspaper className="w-10 h-10 text-primary/30" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="text-lg font-heading font-bold text-primary mb-2 line-clamp-2 group-hover:text-accent transition-colors">
                      {a.title}
                    </h3>
                    {a.excerpt && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{a.excerpt}</p>}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {a.author_name && (
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{a.author_name}</span>
                      )}
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(a.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Articles;
